import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExecutionResult } from 'graphql';
import { getCurrentUserIsSystemUser } from 'src/app/authentication/state/authentication.selector';
import { AudioPlayerComponent, AudioPlayerData } from 'src/app/common-components/audio-player/audio-player.component';
import { CallDetailDialogComponent } from 'src/app/common-components/call-details-dialog/call-detail-dialog.component';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { getCallExtension } from 'src/app/common-components/helpers/call-logs-helper';
import { getHangUpCallsPrivileges } from 'src/app/privileges/privileges.selector';
import { GetRelatedCallDetail } from '../../active-calls/queries/generated/GetRelatedCallDetail';
import { ActiveCallsRegisterApiService } from '../../active-calls/services/active-calls-register-api.service';
import { ActiveCallRegisterType } from '../../active-calls/services/active-calls-register-session-storage.service';
import { CallLogsFilterOptions } from '../models/call-logs-filter-option';
import { CallLogsOrderingOptions, CallLogsRegisterOptions } from '../models/call-logs-register-options';
import { CallLogsRegisterListItem, CallLogsRegisterResults } from '../models/call-logs-register-results.model';
import { GetCallLogs, GetCallLogs_callDetailsConnection_items } from '../queries/generated/GetCallLogs';
import { CallLogsRegisterApiService } from '../services/call-logs-register-api.service';
import * as CallLogsDetailActions from './call-logs-register.actions';
import { callLogsRegisterAdapterActions } from './call-logs-register.actions';
import {
   CallLogsRegisterState,
   selectEndCursor,
   selectOrderingOptions,
   selectors,
   selectQuickFilters,
} from './call-logs-register.reducer';
import * as CallLogsRegisterSelectors from './call-logs-register.selectors';
import { selectFilters } from './call-logs-register.selectors';

@Injectable()
export class CallLogsRegisterEffects {
   loadCallLogsList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callLogsRegisterAdapterActions.pageEnter, CallLogsDetailActions.deleteCallRecordingSuccess),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, endCursor, quickFilters, orderingOptions]) => {
            return this.callLogsRegisterApiService.getCallLogs(null, filters, quickFilters, orderingOptions).pipe(
               map((result) => {
                  return callLogsRegisterAdapterActions.loadSuccess(
                     this.convertGetCallLogsResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(callLogsRegisterAdapterActions.loadFailure(error));
               })
            );
         })
      );
   });

   loadCallLogsPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callLogsRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),

         switchMap(([action, endCursor, quickFilters, filters, orderingOptions]) => {
            return this.callLogsRegisterApiService.getCallLogs(endCursor, filters, quickFilters, orderingOptions).pipe(
               map((result) => {
                  return callLogsRegisterAdapterActions.loadPageSuccess(
                     this.convertGetCallLogsResultToLoadSuccessProps(result.data)
                  );
               }),
               catchError((error) => {
                  return of(callLogsRegisterAdapterActions.loadPageFailure(error));
               })
            );
         })
      );
   });

   openDialog$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(CallLogsDetailActions.openDialog),
            withLatestFrom(
               this.store.pipe(select(getCurrentUserIsSystemUser)),
               this.store.pipe(select(getHangUpCallsPrivileges))
            ),
            switchMap(([action, isSystemUser, canHangupCalls]) => {
               const relatedCallDetailIds = action.callLogs.queueHistoryCallDetailsConnection?.items
                  .reduce(
                     (acc: any[], qh) => [...acc, ...(qh.queueHistory.queueHistoryCallDetailsConnection?.items ?? [])],
                     []
                  )
                  .filter((qhcd) => qhcd.callDetailId !== action.callLogs.callDetailId)
                  .map((qhcd) => qhcd.callDetailId)
                  .filter((id, index, self) => self.indexOf(id) === index);

               if (!relatedCallDetailIds?.length) {
                  return of({
                     callLogs: action.callLogs,
                     isSystemUser,
                     canHangupCalls,
                  });
               }

               return forkJoin(
                  relatedCallDetailIds.map((id) =>
                     this.activeCallsRegisterApiService.getRelatedCallDetailForCallback(id)
                  )
               ).pipe(
                  map((relatedResults: ExecutionResult<GetRelatedCallDetail>[]) => ({
                     callLogs: {
                        ...action.callLogs,
                        relatedCallDetails: relatedResults.map((res) => res.data?.callDetail).filter(Boolean),
                     },
                     isSystemUser,
                     canHangupCalls,
                  }))
               );
            }),
            withLatestFrom(
               this.store
                  .pipe(select(selectors.selectAll))
                  .pipe(select((callLogs) => callLogs.map((cl) => cl.callDetailId)))
            ),
            switchMap(([{ callLogs, isSystemUser, canHangupCalls }, allCallLogIds]) => {
               const ref = this.dialog.open(CallDetailDialogComponent, {
                  data: {
                     callLogs: callLogs,
                     isSystemUser,
                     canHangupCalls,
                     allCallLogIds,
                     registerType: ActiveCallRegisterType.AccountHistory,
                  },
                  width: '800px',
                  autoFocus: false,
               });
               return ref.afterClosed().pipe(
                  tap((result) => {
                     if (result?.navigateTo) {
                        const callLogs$ = this.store.pipe(
                           select(selectors.selectAll),
                           take(1),
                           map((callLogs) => callLogs.find((cl) => cl.callDetailId === result.navigateTo))
                        );
                        callLogs$.subscribe((callLogs) => {
                           if (callLogs) {
                              this.store.dispatch(CallLogsDetailActions.openDialog({ callLogs }));
                           }
                        });
                     }
                  })
               );
            })
         );
      },
      { dispatch: false }
   );

   listenToRecording$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(CallLogsDetailActions.listenToRecording),
            tap((action) => {
               let data: Array<any> = [];
               action.callRecording.files.forEach((item) => {
                  data.push({ file: item, title: 'Call Recording' });
                  if (data.length === action.callRecording.files.length) {
                     const audioPlayerData: AudioPlayerData = {
                        files: data,
                        callRecordingId: action.callRecording.callRecordingId,
                     };
                     this.dialog.open(AudioPlayerComponent, {
                        data: audioPlayerData,
                     });
                  }
               });
            })
         );
      },
      { dispatch: false }
   );

   deleteCallRecording$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(CallLogsDetailActions.deleteCallRecording),
         switchMap((action) => {
            const message = 'Are you sure you want to delete this recording?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Delete Call Recording',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.callLogsRegisterApiService.deleteCallRecording(action.callRecordingId).pipe(
                        map(() => {
                           return CallLogsDetailActions.deleteCallRecordingSuccess(action);
                        }),
                        catchError((error) => {
                           return of(CallLogsDetailActions.deleteCallRecordingFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteDeviceSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(CallLogsDetailActions.deleteCallRecordingSuccess),
            tap((action) => {
               this.snackBar.open(`Call Recording deleted`);
            })
         );
      },
      { dispatch: false }
   );

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callLogsRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(CallLogsRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(callLogsRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions)),
            this.store.pipe(select(selectFilters))
         ),
         switchMap(([action, quickfilters, orderingOptions, filters]) => {
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   constructor(
      private actions$: Actions,
      private store: Store<CallLogsRegisterState>,
      private callLogsRegisterApiService: CallLogsRegisterApiService,
      private activeCallsRegisterApiService: ActiveCallsRegisterApiService,
      private dialog: MatDialog,
      private confirmationService: ConfirmationDialogService,
      private snackBar: MatSnackBar
   ) {}

   private loadNewData(
      filters: CallLogsFilterOptions[],
      quickFilters: CallLogsRegisterOptions | null = null,
      orderingOptions: CallLogsOrderingOptions[] | null = null
   ) {
      return this.callLogsRegisterApiService.getCallLogs(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return callLogsRegisterAdapterActions.loadSuccess(
               this.convertGetCallLogsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callLogsRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(endCursor: string | null, filters: any = null) {
      return this.callLogsRegisterApiService.getCallLogs(endCursor, filters, null, null).pipe(
         map((result) => {
            return callLogsRegisterAdapterActions.loadPageSuccess(
               this.convertGetCallLogsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(callLogsRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetCallLogsResultToLoadSuccessProps(getCallLogs: GetCallLogs): CallLogsRegisterResults {
      return {
         endCursor: getCallLogs.callDetailsConnection.pageInfo.endCursor,
         hasNextPage: getCallLogs.callDetailsConnection.pageInfo.hasNextPage,
         items: getCallLogs.callDetailsConnection.items.map(this.convertCallLogsRegisterListItem),
         totalCount: getCallLogs.callDetailsConnection.totalCount,
      };
   }

   private convertCallLogsRegisterListItem(
      getCallLogsItem: GetCallLogs_callDetailsConnection_items
   ): CallLogsRegisterListItem {
      const firstCallSegment = getCallLogsItem.callSegmentsConnection.items[0];
      const callSegments = getCallLogsItem.callSegmentsConnection.items;

      const isPhoneCallThatWentDirectlyToSourceExtension =
         getCallLogsItem.sourceExtension?.extensionId === getCallLogsItem.destinationExtension?.extensionId &&
         callSegments.length === 1;

      return {
         callDestination: getCallExtension(
            { ...getCallLogsItem.destinationExtension },
            getCallLogsItem.callDirectionTypeId,
            getCallLogsItem.destinationNumber,
            true
         ),
         callDetailId: firstCallSegment?.callDetailId,
         callDirectionTypeId: getCallLogsItem.callDirectionTypeId,
         callDispositionTypeId: getCallLogsItem.callDispositionTypeId,
         callRecordingId: getCallLogsItem.callRecording?.callRecordingId,
         callRecordingFile: getCallLogsItem.callRecording?.file ? { ...getCallLogsItem.callRecording.file } : null,
         callSegmentId: firstCallSegment?.callSegmentId,
         callSegmentsConnection: getCallLogsItem.callSegmentsConnection,
         queueHistoryCallDetailsConnection: getCallLogsItem.queueHistoryCallDetailsConnection,
         callSource: getCallExtension(
            { ...getCallLogsItem.sourceExtension },
            getCallLogsItem.callDirectionTypeId,
            getCallLogsItem.sourceNumber
         ),
         destinationExtension: getCallLogsItem.destinationExtension,
         destinationNumber: getCallLogsItem.destinationNumber,
         phoneNumber: getCallLogsItem.phoneNumber?.phoneNumber ?? null,
         endTime: getCallLogsItem.endTime,
         extensionId: firstCallSegment?.extensionId,
         startTime: getCallLogsItem.startTime,
         serverName: getCallLogsItem.serverName ?? null,
         sipCallId: getCallLogsItem.sipCallId ?? null,
         callRate: getCallLogsItem.callRate ?? 0,
         originalDestinationNumber: getCallLogsItem.originalDestinationNumber,
         callRecordingCallDetailsConnection: getCallLogsItem.callRecordingCallDetailConnection,
         stirShakenAttest: getCallLogsItem.stirShakenAttest ?? null,
         stirShakenToken: getCallLogsItem.stirShakenToken ?? null,
         stirShakenVerification: getCallLogsItem.stirShakenVerification,
         destinationIpAddress: getCallLogsItem.destinationIpAddress ?? null,
         providerId: getCallLogsItem.providerId ?? null,
         sourceProviderId: getCallLogsItem.sourceProviderId ?? null,
         destinationProviderId: getCallLogsItem.destinationProviderId ?? null,
         sourceIpAddress: getCallLogsItem.sourceIpAddress ?? null,
         isPhoneCallThatWentDirectlyToSourceExtension,
      };
   }
}
